import { lazy, Suspense, useCallback, useEffect, useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { BookingDateField } from "@/components/ui/BookingDateField";
import { Button } from "@/components/ui/Button";
import { GuestStayModal } from "@/components/bookings/GuestStayModal";
import { PitchPlanningModal } from "@/components/site-plan/PitchPlanningModal";
import { SitePlanCanvas } from "@/components/site-plan/SitePlanCanvas";
import { SitePlanLegend } from "@/components/site-plan/SitePlanLegend";
import { usePageHeader } from "@/layout/usePageHeader";
import { addDaysLocal, formatIsoDateShort, todayLocal } from "@/lib/bookingDates";
import { useCurrentUser } from "@/lib/auth";
import { canWriteGuestPitchBooking } from "@/lib/permissions";
import { useBookingRealtimeRefresh } from "@/lib/useBookingRealtimeRefresh";
import { buildSitePlanPitchOverlays, type SitePlanPitchStatus } from "@/lib/sitePlan";
import type { CreateBookingInitialValues } from "@/pages/bookings/CreateBookingModal";
import { useGuestById } from "@/lib/useGuestById";
import { useSitePlanPageData } from "@/pages/site-plan/useSitePlanPageData";
import type { Booking } from "@/types/camping";

const CreateBookingModal = lazy(() =>
  import("@/pages/bookings/CreateBookingModal").then((m) => ({
    default: m.CreateBookingModal,
  })),
);

const PLANNING_RANGE_DAYS = 60;

export function SitePlanPage() {
  const referenceDateId = useId();
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canEdit = canWriteGuestPitchBooking(groups);
  const today = todayLocal();
  const [referenceDate, setReferenceDate] = useState(today);
  const [selectedPitch, setSelectedPitch] = useState<SitePlanPitchStatus | null>(null);
  const [planningModalOpen, setPlanningModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createBookingInitial, setCreateBookingInitial] =
    useState<CreateBookingInitialValues | null>(null);
  const [stayModalBooking, setStayModalBooking] = useState<Booking | null>(null);

  const data = useSitePlanPageData(referenceDate);

  useEffect(() => {
    setPageMeta({
      title: "Platzplan",
      subtitle: `Belegung · ${formatIsoDateShort(referenceDate)}`,
    });
    return () => setPageMeta(null);
  }, [referenceDate, setPageMeta]);

  useBookingRealtimeRefresh(data.refresh);

  const badgeScale = useMemo(
    () => Number.parseFloat(data.sitePlanData?.badge_scale ?? "1") || 1,
    [data.sitePlanData?.badge_scale],
  );

  const overlays = useMemo(
    () =>
      buildSitePlanPitchOverlays(data.sitePlanData?.pitches ?? []).map((overlay) => ({
        ...overlay,
        electricity_status: data.electricityByPitch[overlay.id] ?? "off",
      })),
    [data.electricityByPitch, data.sitePlanData?.pitches],
  );

  const planningPitch = useMemo(() => {
    if (!selectedPitch) return null;
    return data.pitches.find((p) => p.id === selectedPitch.pitch_id) ?? null;
  }, [selectedPitch, data.pitches]);

  const stayModalGuest = useGuestById(stayModalBooking?.guest_id);

  const stayModalPitch = useMemo(
    () =>
      stayModalBooking?.pitch_id
        ? (data.pitches.find((p) => p.id === stayModalBooking.pitch_id) ?? null)
        : null,
    [stayModalBooking, data.pitches],
  );

  const handlePitchSelect = useCallback(
    (pitchId: string) => {
      const pitch = data.sitePlanData?.pitches.find((p) => p.pitch_id === pitchId) ?? null;
      setSelectedPitch(pitch);
      setPlanningModalOpen(true);

      if (pitch) {
        const rangeFrom = referenceDate < today ? referenceDate : today;
        const rangeToExclusive = addDaysLocal(rangeFrom, PLANNING_RANGE_DAYS);
        void data.loadPitchBookings(pitch.pitch_id, rangeFrom, rangeToExclusive);
      }
    },
    [data.sitePlanData?.pitches, data.loadPitchBookings, referenceDate, today],
  );

  const handlePlanningFreeSlot = useCallback((pitchId: string, from: string, to: string) => {
    setPlanningModalOpen(false);
    setCreateBookingInitial({ pitchId, arrivalDate: from, departureDate: to });
    setCreateModalOpen(true);
  }, []);

  const handlePlanningOccupied = useCallback((booking: Booking) => {
    setPlanningModalOpen(false);
    setStayModalBooking(booking);
  }, []);

  const handlePlanningClose = useCallback(() => {
    setPlanningModalOpen(false);
    setSelectedPitch(null);
  }, []);

  return (
    <div className="site-plan-page flex h-full min-h-0 flex-1 flex-col gap-2 overflow-hidden">
      <div className="site-plan-page__toolbar shrink-0">
        <div className="flex items-center gap-2" data-tour="site-plan-date">
          <BookingDateField
            id={referenceDateId}
            label="Stichtag"
            value={referenceDate}
            onChange={setReferenceDate}
            compact
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setReferenceDate(today)}
          >
            Heute
          </Button>
        </div>
        <div className="min-w-0 flex-1" data-tour="site-plan-legend">
          <SitePlanLegend compact />
        </div>
        <Link
          to="/planner"
          className="shrink-0 text-sm font-medium text-accent hover:underline"
        >
          Belegungsplaner öffnen
        </Link>
      </div>

      {data.sitePlanData?.wet_blocking_active ? (
        <p className="text-sm font-medium text-status-orange">
          Mindestens ein Platz ist durch einen Sperrzeitraum betroffen.
        </p>
      ) : null}

      {data.statusFromFallback ? (
        <p className="text-sm text-status-orange">
          Belegung lokal berechnet (Platzplan-API nicht erreichbar).
        </p>
      ) : null}

      {data.statusLoading ? (
        <p className="text-sm text-label-secondary">Platzplan wird geladen…</p>
      ) : data.statusError ? (
        <p className="text-sm text-status-red">{data.statusError}</p>
      ) : (
        <div
          data-tour="site-plan-canvas"
          className={clsx(
            "site-plan-page__canvas relative flex min-h-0 flex-1 flex-col border border-separator bg-fill-quaternary/20",
          )}
        >
          {data.imageError ? (
            <p className="p-3 text-sm text-status-orange">
              Planbild nicht geladen ({data.imageError}). Polygone werden trotzdem angezeigt.
            </p>
          ) : null}
          {data.imageLoading && !data.planImage ? (
            <p className="p-3 text-sm text-label-secondary">Planbild wird geladen…</p>
          ) : null}
          <SitePlanCanvas
            imageData={data.planImage}
            pitches={overlays}
            badgeScale={badgeScale}
            mode="view"
            selectedPitchId={selectedPitch?.pitch_id ?? null}
            onPitchSelect={handlePitchSelect}
            viewportSize="fill"
            className="site-plan-page__canvas-inner min-h-0 flex-1"
          />
        </div>
      )}

      <PitchPlanningModal
        open={planningModalOpen && !!selectedPitch}
        pitchStatus={selectedPitch}
        pitch={planningPitch}
        bookings={data.pitchBookings}
        bookingsLoading={data.pitchBookingsLoading}
        referenceDate={referenceDate}
        canEdit={canEdit}
        onClose={handlePlanningClose}
        onSelectFree={handlePlanningFreeSlot}
        onSelectOccupied={handlePlanningOccupied}
      />

      {createModalOpen ? (
        <Suspense fallback={null}>
          <CreateBookingModal
            open={createModalOpen}
            initialValues={createBookingInitial}
            pitches={data.pitches}
            categories={data.categories}
            onClose={() => {
              setCreateModalOpen(false);
              setCreateBookingInitial(null);
            }}
            onCreated={() => {
              setCreateModalOpen(false);
              setCreateBookingInitial(null);
              data.refresh();
            }}
          />
        </Suspense>
      ) : null}

      {stayModalBooking ? (
        <GuestStayModal
          open
          booking={stayModalBooking}
          guest={stayModalGuest}
          pitch={stayModalPitch}
          canEdit={canEdit}
          userGroups={groups}
          onInvoiceChanged={data.refresh}
          onClose={() => setStayModalBooking(null)}
          onEditBooking={() => setStayModalBooking(null)}
        />
      ) : null}
    </div>
  );
}
